import { Injectable, computed, inject, signal } from '@angular/core';
import { AdminNavItem } from '../../../models/admin-nav-item.model';
import { AdminMenuStorageService } from './admin-menu-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AdminMenuEditService {
  private readonly adminMenuStorageService = inject(AdminMenuStorageService);

  private readonly persistedItemsState = signal<AdminNavItem[]>([]);
  private readonly draftItemsState = signal<AdminNavItem[]>([]);
  private readonly editingState = signal(false);

  readonly isEditing = computed(() => this.editingState());

  readonly items = computed(() =>
    this.editingState()
      ? this.draftItemsState()
      : this.persistedItemsState()
  );

  initialize(items: AdminNavItem[]): void {
    const normalizedItems = this.adminMenuStorageService.applyPersistedOrder(
      this.cloneWithOrder(items)
    );

    this.persistedItemsState.set(normalizedItems);

    if (this.editingState()) {
      this.draftItemsState.set(this.cloneWithOrder(normalizedItems));
    }
  }

  startEditing(): void {
    if (this.editingState() || !this.persistedItemsState().length) {
      return;
    }

    this.draftItemsState.set(this.cloneWithOrder(this.persistedItemsState()));
    this.editingState.set(true);
  }

  updateDraft(items: AdminNavItem[]): void {
    if (!this.editingState()) {
      return;
    }

    this.draftItemsState.set(this.cloneWithOrder(items));
  }

  cancelEditing(): void {
    if (!this.editingState()) {
      return;
    }

    this.draftItemsState.set([]);
    this.editingState.set(false);
  }

  saveEditing(): void {
    if (!this.editingState()) {
      return;
    }

    const nextItems = this.cloneWithOrder(this.draftItemsState());

    this.adminMenuStorageService.saveOrder(nextItems);
    this.persistedItemsState.set(nextItems);
    this.draftItemsState.set([]);
    this.editingState.set(false);
  }

  private cloneWithOrder(items: AdminNavItem[]): AdminNavItem[] {
    return [...items].map((item, index) => ({
      ...item,
      originalOrder: item.originalOrder ?? item.order ?? index + 1,
      order: index + 1,
    }));
  }
}